import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'partner' | null
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  async function loginWithGoogle() {
    googleProvider.setCustomParameters({ prompt: "select_account" });
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  }

  async function logout() {
    await signOut(auth);
    setUserRole(null);
    setAccessDenied(false);
  }

  async function checkUserAccess(email) {
    try {
      const userDoc = await getDoc(doc(db, "whitelist", email));
      if (userDoc.exists()) {
        return userDoc.data().role; // 'admin' or 'partner'
      }
      return null;
    } catch (err) {
      console.error("Error checking access:", err);
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const role = await checkUserAccess(user.email);
        if (role) {
          setCurrentUser(user);
          setUserRole(role);
          setAccessDenied(false);
        } else {
          setAccessDenied(true);
          setCurrentUser(null);
          setUserRole(null);
          await signOut(auth);
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    loading,
    accessDenied,
    loginWithGoogle,
    logout,
    isAdmin: userRole === "admin",
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
